import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import { mediaQuery } from '@/utils/helpers';
import BlockWrapper from '@/components/BlockWrapper';
import Container from '@/components/Container';
import Title from '@/components/Title';
import Seminar, { SeminarType } from './Seminar';
import Link, { LinkStyle, LinkType } from '@/components/Link';
import Paragraph from '@/components/Paragraph';

export type SeminarTopicSBlockType = {
  __typename: string;
  contentful_id: string;
  name: string;
  title: string;
  subtitleString: {
    subtitle: string;
  };
  node_locale: string;
  allSeminars: {
    nodes: Array<SeminarType>;
  };
  link: LinkType;
  topics: Array<string>;
};

const TitleWrapper = styled.div`
  text-align: center;
`;

const SeminarListBlock = styled.div`
  margin-top: 3.2rem;
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05),
    0px 2px 7px 0px rgba(0, 0, 0, 0.05);
  border-radius: 12px;

  > * {
    padding: 16px;
  }

  ${mediaQuery.md(`
    > * {
      padding: 20px;
    }
  `)};
`;

const CTAWrapper = styled.div`
  margin-top: 3.2rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;

const SeminarTopicSBlock: React.FC<SeminarTopicSBlockType> = ({
  title,
  subtitleString,
  topics,
  link,
}) => {
  const seminarsData = useStaticQuery(graphql`
    query seminarsQuery {
      allSeminars: allSeminarsJson {
        nodes {
          id
          name
          type
          topics
          datetime {
            start
            end
            date
          }
          lang
          jsonId
          location {
            name
          }
          private
          full
        }
      }
    }
  `);

  const onTopicSeminars = seminarsData.allSeminars.nodes
    .filter((seminar: SeminarType) => !seminar.full)
    .filter((seminar: SeminarType) => !seminar.private)
    .filter((seminar: SeminarType) =>
      seminar.topics.some((s) =>
        topics.map((topic) => topic.toLowerCase()).includes(s),
      ),
    );

  return (
    !!onTopicSeminars.length && (
      <BlockWrapper color='White'>
        <Container narrow>
          <TitleWrapper>
            <Title size='xl' level='h2' margin='0 0 2rem'>
              {title}
            </Title>
            {subtitleString && (
              <Paragraph color='GreyBlue' weight='semiBold'>
                {subtitleString.subtitle}
              </Paragraph>
            )}
          </TitleWrapper>

          <SeminarListBlock>
            {onTopicSeminars.slice(0, 3).map((seminar: SeminarType) => (
              <Seminar {...seminar} key={seminar.id}></Seminar>
            ))}
          </SeminarListBlock>

          {link && (
            <CTAWrapper>
              <Link {...link} linkStyle={LinkStyle.SECONDARY} />
            </CTAWrapper>
          )}
        </Container>
      </BlockWrapper>
    )
  );
};

export default SeminarTopicSBlock;
export const SeminarTopicBlockFragmentQuery = graphql`
  fragment SeminarTopicBlockFragment on ContentfulSeminarTopicSBlock {
    __typename
    title
    subtitleString: subtitle {
      subtitle
    }
    link {
      ...LinkFragment
    }
    topics
  }
`;
